/**
 * 错误监控和报告插件
 *
 * 整合了 Sentry 错误监控、易分析埋点、Google Analytics、
 * 会话参数处理以及客户端全局错误捕获功能
 */

import { sessionSetItem } from '@/utils/localStorage';
import * as Sentry from '@sentry/vue';
import { getLocation } from '~/api/module/seo';
import { INVITE_CODE, SESSION_REF, SESSION_SOURCE } from '~/constants/config';

export default defineNuxtPlugin(({ vueApp }) => {
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();

  const url = useRequestURL();
  const isPure =
    PURE_HOST.some(item => item.test(url.host)) || url.searchParams.get('pure') === '1';

  const { privatization, area } = runtimeConfig.public;

  if (privatization || isPure) {
    return {};
  }

  if (import.meta.client) {
    // 来源参数
    if (route?.query?.from) {
      sessionSetItem(SESSION_SOURCE, route.query.from);
    }

    // 推荐码参数
    if (route.query.ref || route.query.via) {
      sessionSetItem(SESSION_REF, route.query.ref || route.query.via);
    }

    // 邀请码参数
    if (route?.query?.code) {
      sessionSetItem(INVITE_CODE, route.query.code);
    }
  }

  if (area === 'US') {
    useHead({
      link: [{ rel: 'preconnect', href: 'https://www.googletagmanager.com' }],
      script: [
        {
          innerHTML:
            "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-TWNCS4ZG');",
        },
      ],
      noscript: [
        {
          textContent:
            '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TWNCS4ZG" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
        },
      ],
    });
  }

  // 其他区域的脚本注入
  if (import.meta.client && !privatization) {
    const { isMobile } = useDevice();

    // 国内版本：polyfill + 七陌客服
    if (isChinese()) {
      useHead({
        script: [
          {
            src: 'https://polyfill.youyan3d.com/polyfill.js?features=es2015,es2016,es2017,es2018,es2019,es2020,es2021,es2022,globalThis,es2023',
            defer: true,
          },
          {
            src: 'https://ykf-webchat.7moor.com/javascripts/7moorInit.js?accessId=b5280620-7c49-11ee-80b7-ff4a5856c7b1&autoShow=false&language=ZHCN',
            async: true,
          },
        ],
      });
    }
    // 海外版本（非移动端）：polyfill + cookie 控制 + Tawk.to + Ahrefs + Google Analytics
    else {
      useHead({
        script: [
          {
            src: 'https://polyfill.rendora.ai/polyfill.js?features=es2015,es2016,es2017,es2018,es2019,es2020,es2021,es2022,globalThis,es2023',
            defer: true,
          },
          {
            src: 'https://fe.rendora.ai/rendora/components/r-gdpr-cookie-consent/1.0.6/bundle.js',
            defer: true,
          },
          {
            innerHTML: `
; (function () {const s1 = document.createElement('script')
const s0 = document.getElementsByTagName('script')[0]
s1.async = true
s1.src = 'https://embed.tawk.to/6661c6b9981b6c5647799e6c/1hvmv0h4k'
s1.charset = 'UTF-8'
s1.setAttribute('crossorigin', '*')
s0.parentNode?.insertBefore(s1, s0)
})()`,
            async: true,
          },
          {
            src: 'https://analytics.ahrefs.com/analytics.js',
            'data-key': 'F236zO7yBqsCByJixUWSKA',
            async: true,
          },
          {
            src: 'https://www.googletagmanager.com/gtag/js?id=G-8WNSVY2PEK',
            async: true,
          },
          {
            innerHTML: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-8WNSVY2PEK');`,
          },
        ],
      });
    }
  }

  // ==================== 3. 客户端初始化 ====================
  let handleErrorFn:
    | ((error: Error, type: string, context?: Record<string, unknown>) => void)
    | undefined;

  if (import.meta.client) {
    // 3.1 易分析埋点（生产环境且国内区域）
    if (process.env.NODE_ENV === 'production' && area === 'CN') {
      useHead({
        script: [
          {
            innerHTML: `var _yfx_trackdata = _yfx_trackdata || [];
var _yfx_website = "10018517";
var _yfx_netrandom = 100;
var _yfx_domrandom = 100;
var _yfx_videoplayerid ="resourceVideo";
(function() {
    var yfxjs = document.createElement("script");
    yfxjs.charset = "utf-8";
    yfxjs.src = "//cdn.yeefx.cn/yeefxcount.js";
    var yfxjs_t = document.getElementsByTagName("script")[0];
    yfxjs_t.parentNode.insertBefore(yfxjs, yfxjs_t);
})();`,
          },
        ],
      });
    }

    if (import.meta.env.PROD) {
      const integrations = [
        Sentry.browserTracingIntegration(),
        Sentry.captureConsoleIntegration({
          levels: ['error'],
        }),
        Sentry.extraErrorDataIntegration(),
      ];

      Sentry.init({
        dsn: 'https://02854a0ae8ea493189e0ddde300e7b0e@xmov-frontend-sentry.xmov.ai/6',
        integrations,
        release: `${runtimeConfig.public.env}:${runtimeConfig.public.release || 'unknown'}`,
        environment: `${runtimeConfig.public.env}:${runtimeConfig.public.area}`,
        tracePropagationTargets: ['localhost', /^https:\/\/.+youyan30/, '192.168'],
        sendDefaultPii: true,
        tracesSampleRate: 1.0,
        app: vueApp,
      });

      // 设置地理位置信息（fire-and-forget，不阻塞 Sentry 初始化）
      getLocation()
        .then(position => {
          Sentry.setContext('geo', {
            city: position.city,
            country_code: position.countryCode,
            region: position.region,
            latitude: position.lat,
            longitude: position.lon,
          });
        })
        .catch(() => {});
    }

    // 3.3 错误处理函数定义
    handleErrorFn = (error: Error, type: string, context?: Record<string, unknown>) => {
      // eslint-disable-next-line no-console
      console.error(`🚨 [CLIENT] ${type}错误:`, {
        message: error.message,
        stack: error.stack,
        name: error.name,
        type,
        context,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      // 发送到 Sentry（如果已初始化）
      if (import.meta.env.PROD && Sentry.getClient()) {
        Sentry.captureException(error, {
          tags: { errorType: type },
          extra: context,
        });
      }
    };

    // 3.4 Vue 错误处理
    vueApp.config.errorHandler = (error: unknown, instance, info: string) => {
      const err = error instanceof Error ? error : new Error(String(error));
      handleErrorFn!(err, 'Vue组件错误', {
        info,
        componentName: instance?.$options?.name,
      });
    };

    vueApp.config.warnHandler = (msg: string, instance, trace: string) => {
      // eslint-disable-next-line no-console
      console.warn('⚠️ [CLIENT] Vue警告:', {
        message: msg,
        trace,
        componentName: instance?.$options?.name,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    };

    // 定义事件处理器，保存引用以便清理
    const globalErrorHandler = (event: ErrorEvent) => {
      handleErrorFn!(event.error || new Error(event.message), '全局JavaScript错误', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      handleErrorFn!(error, '未处理的Promise拒绝', {
        reason: event.reason,
        promise: event.promise,
      });
    };

    const resourceErrorHandler = (event: ErrorEvent) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        handleErrorFn!(new Error(`资源加载失败: ${target.tagName}`), '资源加载错误', {
          tagName: target.tagName,
          src: (target as HTMLImageElement).src || (target as HTMLLinkElement).href,
          type: target.getAttribute('type'),
        });
      }
    };

    // 保存原始 fetch，以便恢复
    const originalFetch = 'fetch' in window ? window.fetch : null;

    // 3.5 全局 JavaScript 错误捕获
    window.addEventListener('error', globalErrorHandler);

    // 3.6 Promise 拒绝错误捕获
    window.addEventListener('unhandledrejection', unhandledRejectionHandler);

    // 3.7 资源加载错误捕获
    window.addEventListener('error', resourceErrorHandler, true);

    // 3.8 网络请求错误监控
    if (originalFetch) {
      window.fetch = async (...args) => {
        try {
          const response = await originalFetch(...args);

          // 记录失败的请求
          if (!response.ok) {
            // eslint-disable-next-line no-console
            console.error('🌐 [CLIENT] 网络请求失败:', {
              url: args[0],
              status: response.status,
              statusText: response.statusText,
              method: args[1]?.method || 'GET',
              timestamp: new Date().toISOString(),
            });
          }

          return response;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('🌐 [CLIENT] 网络请求异常:', {
            url: args[0],
            method: args[1]?.method || 'GET',
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString(),
          });
          throw error;
        }
      };
    }

    // 在应用卸载时清理事件监听器和恢复 fetch
    // 注意：Nuxt 插件通常不需要手动清理，但为了安全起见，我们提供清理机制
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        window.removeEventListener('error', globalErrorHandler);
        window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
        window.removeEventListener('error', resourceErrorHandler, true);
        // 恢复原始 fetch（如果需要）
        if (originalFetch) {
          window.fetch = originalFetch;
        }
      });
    }
  }

  // ==================== 4. 返回插件提供的方法和属性 ====================
  return {
    provide: {
      sentry: Sentry,
      reportError: handleErrorFn
        ? (error: Error, context?: Record<string, unknown>) => {
            handleErrorFn(error, '手动报告错误', context);
          }
        : undefined,
      area: runtimeConfig.public.area,
      foreign: runtimeConfig.public.area !== 'CN',
    },
  };
});
